import React, { useEffect } from "react";
import { useForm } from 'react-hook-form';
import {
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'components/ui/dialog';
import { Button } from "components/ui/button";
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import { Form, FormField, FormItem, FormControl, FormMessage } from 'components/ui/form';
import { useGroupCreate } from "../../hooks/use-iam";
import { v4 as uuidv4 } from 'uuid';
import { useCommonStore } from "state/store/common/useCommonStore";
import { useTranslation } from "react-i18next";

/**
 * AddGroupModal Component
 *
 * A dialog component that displays a form for creating a new group. On successful submission,
 * it calls the reloadData callback to refresh the groups list.
 *
 * Features:
 * - Uses `react-hook-form` for form state and validation
 * - Submits form data via `useGroupCreate` mutation
 * - Styled using Tailwind and ShadCN dialog components
 *
 * Props:
 * - `onClose` (function): Callback to close the dialog
 * - `reloadData` (function, optional): Callback to reload the groups list after creation
 *
 * @param {AddGroupModalProps} props - Component props
 * @returns {JSX.Element} A modal with a group creation form
 *
 * @example
 * <AddGroupModal onClose={() => setIsDialogOpen(false)} reloadData={fetchGroups} />
 */

interface FormData {
    groupName: string;
}

interface AddGroupModalProps {
    isOpen?: boolean;
    onClose: () => void;
    reloadData?: () => void;
}

export const AddGroupModal: React.FC<AddGroupModalProps> = ({
    onClose,
    reloadData,
}) => {
    const { t } = useTranslation();
    const { mutateAsync: createGroup } = useGroupCreate();
    const user = useCommonStore((state) => state.user);

    const form = useForm<FormData>({
        defaultValues: {
            groupName: '',
        },
    });

    const { control, handleSubmit, reset, watch } = form;
    const groupName = watch('groupName');

    // Reset form when dialog opens/closes
    useEffect(() => {
        reset();
    }, [reset]);

    const onSubmit = async (data: FormData) => {
        const itemId = uuidv4();

        const payload = {
            ItemId: itemId,
            GroupName: data.groupName,
            GroupType: "Project",
            UserIds: [],
            OwnerUserId: user?.id || "",
            Source: "internal",
        };

        try {
            await createGroup(payload);
            reloadData?.();
            onClose();
            reset();
        } catch (error) {
            console.error(error);
        }
    };

    const handleDialogClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
    };

    return (
        <DialogContent
            className="rounded-[10px] sm:max-w-[500px]"
            onClick={handleDialogClick}
        >
            <DialogHeader>
                <DialogTitle className="mb-2">{t('ADD_GROUP')}</DialogTitle>
                <DialogDescription className="text-medium-emphasis font-normal">
                    {t('ADD_GROUP_DESCRIPTION')}
                </DialogDescription>
            </DialogHeader>
            <Form {...form}>
                <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
                    <FormField
                        control={control}
                        name="groupName"
                        rules={{
                            required: t('GROUP_NAME_REQUIRED'),
                            validate: {
                                notEmpty: (value) => value.trim().length > 0 || t('GROUP_NAME_CANNOT_BE_EMPTY')
                            },
                            maxLength: {
                                value: 50,
                                message: t('MAX_50_CHARACTERS_GROUP_NAME')
                            }
                        }}
                        render={({ field }) => (
                            <FormItem>
                                <Label>{t('GROUP_NAME')}*</Label>
                                <FormControl>
                                    <Input className="
                                        h-10 w-full rounded-3xl
                                        border border-gray-300
                                        bg-background pr-8
                                        focus:outline-none
                                        focus:border-primary
                                        focus:ring-1 focus:ring-primary
                                    " {...field} placeholder={t('ENTER_GROUP_NAME')} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <DialogFooter className="mt-5 flex justify-end gap-2">
                        <Button variant="outline" type="button" onClick={onClose}>
                            {t('CANCEL')}
                        </Button>
                        <Button type="submit" disabled={!groupName || groupName.trim().length === 0}>
                            {t('ADD_GROUP')}
                        </Button>
                    </DialogFooter>
                </form>
            </Form>
        </DialogContent>
    );
};